import {
  calculateGranularityUnit,
  type GranularityType,
  groupDataBasedOnGranularity,
} from "@tokenterminal/tt-analytics-api-types/dist/utils/granularity"
import { dequal } from "dequal"
import { useContext, useEffect, useRef, useState } from "react"
import useIsMobile from "../../../ui/combobox/useIsMobile"
import { type ThemeMode, useThemeContext } from "../../../ui/theme/ThemeContext"
import { useXAxisOptions } from "./axes/xAxes"
import { ChartContext } from "./ChartContext"
import { SERIE_STACK } from "./ChartOptions"
import { COLORS_LARGE_SET } from "./colors"
import EmptyState from "./EmptyState"
import { ExpandedTooltipModal } from "./ExpandedTooltipModal"
import NoDataEmptyState from "./NoDataEmptyState"
import type { TooltipOptions } from "./Tooltip"
import type { TooltipExpandedData } from "./types"
import { useElementSize } from "./useElementSize"
import {
  type ChartSerie,
  type ChartSerieNonTimeData,
  type ChartSerieTimeData,
  convertChartDataToAggregatedData,
  createChartSerie,
  useYAxisOptions,
} from "./useHighchartOptions"
import type { Highcharts } from "./useHighcharts"
import { useInitializeHighcharts } from "./useInitializeHighcharts"
import { createSerieId } from "./utils/createSerieId"
import { fixCumulativeData } from "./utils/fixCumulativeData"
import { getPercentChangeSeries } from "./utils/getPercentChangeSeries"
import {
  hideDataBeforeCurrentPeriod,
  hideInitialPartialPeriod,
} from "./utils/hideDataNotInCurrentPeriod"
import { Watermark } from "./Watermark"

/** @deprecated */
export type {
  ChartMetricApproximation,
  GranularityType,
} from "@tokenterminal/tt-analytics-api-types/dist/utils/granularity"
/** @deprecated */
export type YAxisOptions = Highcharts.YAxisOptions
/** @deprecated */
/** @deprecated */
export type {
  ChartSerie,
  ChartSerieData,
  ChartSerieNonTimeData,
  ChartSerieTimeData,
} from "./useHighchartOptions"

/** Format could be hex strings or chart color palette color ID (e.g.
 * `$tt-chart-color-1`). Color palette IDs can be parsed using the
 * getColorPaletteColorVariableById or computeVariableByColorId helpers. */
/** @deprecated */
export type ChartSerieColor = string

/** @deprecated */
export type ChartProps<T extends boolean = boolean> = {
  series: Array<Omit<ChartSerie, "data">>
  seriesData: T extends boolean
    ? T extends false
      ? Record<string, ChartSerieNonTimeData>
      : Record<string, ChartSerieTimeData>
    : never
  showNavigator?: boolean
  zoom: number
  granularity?: GranularityType
  xAxisOptions?: Partial<Highcharts.XAxisOptions>
  yAxisOptions?:
    | Partial<Highcharts.YAxisOptions>
    | Array<Partial<Highcharts.YAxisOptions>>
  tooltipOptions?: Partial<TooltipOptions>
  aggregated?: boolean
  isTimeBased?: T
  colors?: Array<ChartSerieColor>
  legend?: boolean
  showCurrentPeriod?: boolean
  hidePartialPeriod?: boolean
  isBarChart?: boolean
  reverseBarOrder?: boolean
  disableClickToOpenModal?: boolean
  enableDataGrouping?: boolean
}

/** @deprecated */
export type ChartPropsTime = Omit<ChartProps<true>, "isTimeBased"> & {
  isTimeBased?: true
}
/** @deprecated */
export type ChartPropsNonTime = Omit<ChartProps<false>, "isTimeBased"> & {
  isTimeBased: false
}

/** @deprecated */
export type ChartPropsUnion = ChartPropsTime | ChartPropsNonTime

function getSerieColor(colors: Array<string>, index: number) {
  return colors[index % colors.length]
}

type PropCache = {
  granularity: ChartPropsUnion["granularity"]
  aggregated: ChartPropsUnion["aggregated"]
  isTimeBased: ChartPropsUnion["isTimeBased"]
  seriesColorPalette: ChartPropsUnion["colors"]
  series: ChartPropsUnion["series"]
  seriesData: ChartPropsUnion["seriesData"]
  showCurrentPeriod: ChartPropsUnion["showCurrentPeriod"]
  zoom: ChartPropsUnion["zoom"]
  theme: ThemeMode | undefined
  enableDataGrouping: boolean
}

function arrayMinMax(arr: number[]): [number, number] {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  for (let i = 0; i < arr.length; i++) {
    const val = arr[i] ?? 0
    if (val < min) min = val
    if (val > max) max = val
  }

  return [min, max]
}

/** @deprecated */
export function Chart({
  series,
  seriesData,
  showNavigator = true,
  zoom,
  granularity = "day",
  xAxisOptions: extraXAxisOptions,
  yAxisOptions: extraYAxisOptions,
  aggregated = false,
  isTimeBased = true,
  colors: seriesColorPalette = COLORS_LARGE_SET,
  tooltipOptions,
  legend = true,
  showCurrentPeriod = true,
  isBarChart = false,
  hidePartialPeriod = false,
  reverseBarOrder = false,
  disableClickToOpenModal = false,
  enableDataGrouping = false,
}: ChartPropsUnion): React.ReactNode {
  const chartParentContainerRef = useRef<HTMLDivElement>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const currentGranularity = useRef<string>(granularity)
  const cache = useRef<PropCache>({
    series: [],
    seriesData: {},
    granularity,
    aggregated,
    isTimeBased,
    seriesColorPalette,
    showCurrentPeriod,
    zoom,
    theme: undefined,
    enableDataGrouping,
  })

  const xAxisCache = useRef<Partial<Highcharts.XAxisOptions> | null>(null)
  const [isMounted, setMounted] = useState(false)
  const [expandedTooltipData, setExpandedTooltipData] =
    useState<TooltipExpandedData | null>(null)
  const { setApi, setChartData } = useContext(ChartContext)
  const { theme } = useThemeContext()

  const isMobile = useIsMobile()
  const chartType = isTimeBased ? "stockChart" : "chart"

  const xAxisOptions = useXAxisOptions(
    isTimeBased,
    granularity,
    extraXAxisOptions,
    isBarChart,
    reverseBarOrder
  )
  const yAxisOptions = useYAxisOptions(
    series,
    {
      isMobile,
      aggregated,
    },
    extraYAxisOptions
  )

  const hasPercentageSeries = series.some((serie) => serie.percentage)

  const handleChartPointClick = (data: TooltipExpandedData) => {
    // Only expand multi-series points; a single-series modal just duplicates
    // the tooltip.
    if (data.points.length <= 1) return
    setExpandedTooltipData(data)
  }

  const chartInstanceRef = useInitializeHighcharts(chartContainerRef, {
    chartType,
    showNavigator,
    tooltipOptions: tooltipOptions ?? {},
    legend,
    isBarChart,
    hasPercentageSeries,
    // On mobile a tap == a click; skip the modal so a tap shows the tooltip.
    onChartPointClick:
      disableClickToOpenModal || isMobile ? undefined : handleChartPointClick,
  })

  const redrawChartRef = useRef(false)

  const chartInstance = chartInstanceRef.current

  // biome-ignore lint/correctness/useExhaustiveDependencies: chartInstance (ref.current) must be in deps to re-run when Highcharts instance is created
  useEffect(
    function onMount() {
      if (chartInstanceRef.current && !isMounted) {
        setMounted(true)
        setApi(chartInstanceRef.current)
      }
    },
    [chartInstance, isMounted, setApi, chartInstanceRef]
  )
  useEffect(
    function updateAxis() {
      const chartInstance = chartInstanceRef.current
      if (!chartInstance || !isMounted) {
        return
      }

      if (!dequal(xAxisCache.current, xAxisOptions)) {
        xAxisCache.current = xAxisOptions
        chartInstance.xAxis[0].update(xAxisOptions, false)
        redrawChartRef.current = true
      }

      // 10/23/2024 - commenting out as this seems to be causing issues
      //              with removing the first series when there are multiple
      //              causing Error: Highcharts error #18: www.highcharts.com/errors/18/
      // for (const axis of chartInstance.yAxis) {
      //   const existingYAxis = yAxisOptions.find(
      //     (yAxisOption) => axis.userOptions.id === yAxisOption.id,
      //   )

      //   if (!existingYAxis && axis.userOptions.id !== 'navigator-y-axis') {
      //     axis.remove(false)
      //     redrawChartRef.current = true
      //   }
      // }

      for (const yAxisOption of yAxisOptions) {
        const existingYAxis =
          yAxisOption.id === undefined
            ? undefined
            : (chartInstance.get(yAxisOption.id) as Highcharts.Axis | undefined)
        if (existingYAxis) {
          existingYAxis.update(
            {
              // force offset to be undefined, helps with proper redrawing
              offset: undefined,
              ...yAxisOption,
            },
            false
          )
        } else {
          chartInstance.addAxis(yAxisOption, false, false, false)
        }
        redrawChartRef.current = true
      }
    },
    [xAxisOptions, yAxisOptions, isMounted, chartInstanceRef]
  )

  useEffect(
    function AddAndRemoveSeries() {
      const chartInstance = chartInstanceRef.current
      if (!chartInstance || !isMounted) {
        return
      }

      if (
        dequal(cache.current, {
          granularity,
          zoom,
          showCurrentPeriod,
          aggregated,
          isTimeBased,
          seriesColorPalette,
          series,
          seriesData,
          theme,
          enableDataGrouping,
        })
      ) {
        return
      }

      cache.current = {
        granularity,
        zoom,
        aggregated,
        isTimeBased,
        seriesColorPalette,
        series,
        seriesData,
        showCurrentPeriod,
        theme,
        enableDataGrouping,
      }
      cache.current.series = JSON.parse(JSON.stringify(series))
      cache.current.seriesData = JSON.parse(JSON.stringify(seriesData))

      let modifiedSeries = [...series]
      let modifiedSeriesData = seriesData
      if (isTimeBased && aggregated && series.length) {
        modifiedSeries = [
          {
            ...series[0],
            label: series[0].yAxis,
            name: series[0].yAxis,
          },
        ]
        modifiedSeriesData = {
          [cache.current.series[0].yAxis]: convertChartDataToAggregatedData(
            Array.from(Object.values(seriesData)) as Array<ChartSerieTimeData>
          ),
        }
      }

      const newSeries = modifiedSeries.map((serie) => createSerieId(serie.name))
      const currentSeries =
        chartInstance.series
          ?.map((serie) => serie.options.id)
          .filter((id) => id !== undefined) ?? []

      for (const serie of currentSeries) {
        if (!newSeries.includes(serie)) {
          const serieToRemove = chartInstance.get(serie) as Highcharts.Series
          serieToRemove?.remove(false, false)
          redrawChartRef.current = true
        }
      }

      // make sure the charts have the same values
      if (Object.keys(modifiedSeriesData).length && isTimeBased) {
        let serieWithAllZoom: ChartSerieTimeData = []
        Object.values(modifiedSeriesData).forEach((data) => {
          if (serieWithAllZoom.length < data.length) {
            serieWithAllZoom = data
          }
        })

        Object.values(
          modifiedSeriesData as Record<string, ChartSerieTimeData>
        ).forEach((data) => {
          if (serieWithAllZoom.length !== data.length) {
            const allAvailableTimestampsInCurrentSerie = data.map(
              (row) => row[0]
            )

            serieWithAllZoom.forEach((row) => {
              if (!allAvailableTimestampsInCurrentSerie.includes(row[0])) {
                data.push([row[0], null])
              }
            })
            data.sort((a, b) => a[0] - b[0])
          }
        })
      }

      const modifiedData = modifiedSeries.map((serie, index) => {
        let serieData = modifiedSeriesData[serie.name] ?? []

        if (isTimeBased && granularity !== "day") {
          // When grouping the xAxis misbehaves sometimes, leading it to show an empty gap at the end
          // @see https://jsfiddle.net/fwxq8yrk/217/
          serieData = groupDataBasedOnGranularity(
            serieData as ChartSerieTimeData,
            {
              approximation: serie.groupingApproximation,
              unit: calculateGranularityUnit(granularity),
            },
            zoom
          )

          if (!showCurrentPeriod) {
            serieData = hideDataBeforeCurrentPeriod(
              serieData as ChartSerieTimeData,
              granularity
            )
          }

          if (hidePartialPeriod) {
            serieData = hideInitialPartialPeriod(
              serieData as ChartSerieTimeData,
              granularity
            )
          }
        }

        // Temporary fix to handle cumulative until https://github.com/highcharts/highcharts/issues/18010 is fixed
        if (serie.cumulative && isTimeBased) {
          serieData = fixCumulativeData(serieData as ChartSerieTimeData, "day")
        }

        // Calculate relative percentage for time-based percentage series
        if (
          serie.percentage &&
          isTimeBased &&
          serie.stack !== SERIE_STACK.PERCENTAGE
        ) {
          // For non-stacked series, calculate percentage change relative to first non-null value

          serieData = getPercentChangeSeries({
            serie,
            serieData: serieData as ChartSerieTimeData,
            granularity,
          })
        }

        let serieToUpdate = chartInstance.get(createSerieId(serie.name)) as
          | Highcharts.Series
          | undefined

        const groupPadding = 0
        if (serieToUpdate) {
          if (
            currentGranularity.current === granularity &&
            serieToUpdate.points?.length > 0
          ) {
            const previousData = serieToUpdate.points.map((point) => [
              point.x,
              point.y,
            ])

            if (!dequal(serieData, previousData)) {
              // TODO: figure out animation
              serieToUpdate.update(
                {
                  ...createChartSerie(
                    {
                      color: getSerieColor(seriesColorPalette, index),
                      ...serie,
                    },
                    {
                      granularity,
                      enableDataGrouping,
                    }
                  ),
                  data: serieData,
                  groupPadding,
                } as Highcharts.SeriesOptionsType,
                false
              )
            } else {
              serieToUpdate.update(
                createChartSerie(
                  { color: getSerieColor(seriesColorPalette, index), ...serie },
                  {
                    granularity,
                    enableDataGrouping,
                  }
                ),
                false
              )
            }
          } else {
            serieToUpdate.update(
              {
                ...createChartSerie(
                  {
                    color: getSerieColor(seriesColorPalette, index),
                    ...serie,
                  },
                  {
                    granularity,
                    enableDataGrouping,
                  }
                ),
                data: serieData,
                groupPadding,
              } as Highcharts.SeriesOptionsType,
              false
            )
            // serieToUpdate.setData(serieData, true, true)
          }
        } else {
          serieToUpdate = chartInstance.addSeries(
            {
              ...createChartSerie(
                {
                  color: getSerieColor(seriesColorPalette, index),
                  ...serie,
                },
                {
                  granularity,
                  enableDataGrouping,
                }
              ),
              data: serieData,
              groupPadding,
            } as Highcharts.SeriesOptionsType,
            false
          )
        }

        const seriesYAxisValues = serieData
          .map((row) => row[1])
          .filter((x): x is number => x !== null)

        const [dataMin, dataMax] = arrayMinMax(seriesYAxisValues)

        //   // if all values are zero (possible for example on token incentives)
        //   // we change the type into line so it is visible on bottom of chart
        if (
          dataMin === 0 &&
          dataMax === 0 &&
          serieToUpdate.options.type !== "line"
        ) {
          serieToUpdate.update(
            {
              type: "line",
            },
            false
          )
        }

        // @ts-expect-error it's available on certain types and that's enough for us
        if (serieToUpdate.options.stacking === SERIE_STACK.PERCENTAGE) {
          serieToUpdate.yAxis.update(
            {
              min: dataMin < 0 ? -100 : 0,
              max: 100,
            },
            false
          )
        } else {
          serieToUpdate.yAxis.update(
            {
              min: undefined,
              max: undefined,
            },
            false
          )
        }

        redrawChartRef.current = true
        currentGranularity.current = granularity
        return serieData
      })

      setChartData(
        series,
        modifiedData as unknown as
          | Array<ChartSerieNonTimeData>
          | Array<ChartSerieTimeData>,
        isTimeBased
      )
    },
    [
      granularity,
      zoom,
      aggregated,
      isTimeBased,
      seriesColorPalette,
      series,
      seriesData,
      isMounted,
      chartInstanceRef,
      showCurrentPeriod,
      setChartData,
      theme,
      hidePartialPeriod,
      enableDataGrouping,
    ]
  )

  const hasSeriesData = Object.values(seriesData).some(
    (data) => data.length > 0
  )
  const hasVisibleSeries = series.some((serie) => serie.visible !== false)

  // Highcharts measures its container in whole pixels, so floor the size here
  // too, or the same-size check below never matches a fractional width.
  const size = useElementSize(chartParentContainerRef, 5)
  const width = Math.floor(size.width)
  const height = Math.floor(size.height)

  useEffect(() => {
    if (!chartInstance) {
      return
    }
    // setSize redraws the whole chart, even at the same size. This effect
    // also runs on mount, where the redraw effect below draws the data once.
    if (
      chartInstance.chartWidth === width &&
      chartInstance.chartHeight === height
    ) {
      return
    }
    // Shows up as a `measure` span in Sentry.
    const start = performance.now()
    chartInstance.setSize(width, height, false)
    // Redraw is needed after setSize to properly reposition axes
    chartInstance.redraw(false)
    performance.measure("chart-resize", { start })
  }, [width, height, chartInstance])

  // Handle redraws triggered by other effects (axis updates, series changes, etc.)
  useEffect(() => {
    const chartInstance = chartInstanceRef.current
    if (!redrawChartRef.current || !chartInstance) {
      return
    }

    redrawChartRef.current = false

    // Remove empty y-axes that don't have any series
    chartInstance.yAxis.forEach((axis) => {
      if (!axis.series.length && !axis.options.id?.startsWith("navigator-")) {
        axis.remove(false)
      }
    })

    // Highcharts wants to keep current zoom so we need to reset it
    // when users didn't set it themselves.
    if (chartInstance.xAxis[0]) {
      const extremes = chartInstance.xAxis[0].getExtremes()
      if (
        (!extremes.userMin && !extremes.userMax) ||
        extremes.userMin === extremes.dataMin ||
        extremes.userMax === extremes.dataMax
      ) {
        chartInstance.xAxis[0].setExtremes(undefined, undefined, false, false)
      }
    }

    // Shows up as a `measure` span in Sentry.
    const start = performance.now()
    chartInstance.redraw(false)
    performance.measure("chart-redraw", { start })
  })

  let emptyState = null
  if (!hasSeriesData) {
    emptyState = <NoDataEmptyState />
  } else if (!hasVisibleSeries) {
    emptyState = (
      <EmptyState
        headingText="No legend items selected"
        bodyText="To see data, select at least one legend item."
        size="small"
      />
    )
  }
  const shouldRenderChart = hasSeriesData && hasVisibleSeries

  return (
    <>
      <div
        className="z-1 max-w-full h-full relative"
        ref={chartParentContainerRef}
      >
        {emptyState && (
          <div className="flex items-center justify-center h-full">
            {emptyState}
          </div>
        )}
        <div
          className="h-full absolute top-0 bottom-0"
          ref={chartContainerRef}
          style={{
            right: -5,
            left: -5,
            width: `${width}px`,
            height: `${height}px`,
          }}
        />
        {shouldRenderChart && (
          <Watermark
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none w-full"
            style={{ opacity: 0.4 }}
          />
        )}
      </div>
      <ExpandedTooltipModal
        isOpen={expandedTooltipData !== null}
        onClose={() => setExpandedTooltipData(null)}
        data={expandedTooltipData}
      />
    </>
  )
}
